import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { useAlert } from 'components/Shared/Alert'
import { useAppDispatch } from 'redux/Hook'
import { updateOwnerToggles } from '../../Helpers/InitialDataLoader'
import {
  backfillSentiment,
  fetchContributions,
  sentimentSummary,
} from 'redux/thunks/TextAnalysis'

// 0 if the toggle is disabled at platform level
type ToggleState = {
  sentiment: boolean | '0'
  thematic: boolean | '0'
}

type UseSentimentToggleProps = {
  ownerConfigId: string
  handleClose: () => void
}

const handleEnableSentiment = async (
  dispatch: ReturnType<typeof useAppDispatch>,
  location: ReturnType<typeof useLocation>,
  handleClose: () => void,
  showAlert: ReturnType<typeof useAlert>['showAlert'],
) => {
  const { search } = location
  const urlParams = new URLSearchParams(search)

  try {
    const response = await dispatch(backfillSentiment())
    const { payload } = response
    const tabId = Number(urlParams.get('tab_id'))

    if (!payload || (payload.status !== 200 && payload.status !== 409)) {
      showAlert({
        type: 'error',
        title: 'Failed to backfill sentiment for all contributions',
      })
      return false
    }

    if (payload.status === 409) {
      showAlert({
        type: 'warning',
        title: 'Sentiment update in progress - please wait',
      })
      handleClose()
      return false
    }

    if (payload.status === 200) {
      if (tabId === 0) {
        // Slight delay to allow the backfill to complete
        setTimeout(() => {
          dispatch(fetchContributions())
        }, 100)
      } else if (tabId === 2) {
        dispatch(sentimentSummary())
      }
    }

    // Apply toggle updates for both 200 and 409
    handleClose()
    return true
  } catch (error) {
    showAlert({
      type: 'error',
      title: 'Failed to backfill sentiment for all contributions',
    })
    return false
  }
}

export const useAnalyticsToggles = ({
  ownerConfigId,
  handleClose,
}: UseSentimentToggleProps) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()

  const handleSave = useCallback(
    async (toggles: ToggleState) => {
      let allHandlersSucceeded = true
      try {
        const featureToggles = Object.entries(toggles).reduce(
          (acc, [key, value]) => {
            acc[key] = value === true
            return acc
          },
          {} as Record<string, boolean>,
        )

        // Updating both the backend and local Redux state for toggles.
        await updateOwnerToggles(dispatch, featureToggles, ownerConfigId)

        // Feature-specific side-effects
        const handlers: Record<string, (enabled: boolean) => Promise<boolean>> =
          {
            sentiment: async (enabled) => {
              if (enabled) {
                return await handleEnableSentiment(
                  dispatch,
                  location,
                  handleClose,
                  showAlert,
                )
              } else {
                handleClose()
                return true
              }
            },
            // future: thematic, customX, etc.
          }

        for (const [key, value] of Object.entries(featureToggles)) {
          const handler = handlers[key]
          if (handler) {
            const result = await handler(value)
            if (!result) allHandlersSucceeded = false
          }
        }

        if (allHandlersSucceeded) {
          showAlert({
            type: 'success',
            title: 'AI analysis settings updated successfully.',
          })
        }
      } catch (error) {
        showAlert({
          type: 'error',
          title: 'Something went wrong while updating AI settings.',
        })
      }
    },
    [location, ownerConfigId, handleClose, showAlert, dispatch],
  )

  return { handleSave }
}
